<template>
  <div class="internal-billing">
    <EntraPageHeader
      title="Internal Billing (Options IT)"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadAll"
    />

    <div class="page-body">
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-else-if="loading" class="loading-state">
        <span>Loading internal billing data...</span>
      </div>

      <template v-else>
        <!-- ===== Time Range ===== -->
        <div class="dashboard-header">
          <div class="range-tabs">
            <button
              v-for="range in timeRanges"
              :key="range.value"
              class="range-tab"
              :class="{ active: selectedRange === range.value }"
              @click="selectRange(range.value)"
            >{{ range.label }}</button>
          </div>
          <div class="currency-filter">
            <label class="currency-filter-label">Currency:</label>
            <select v-model="selectedCurrency" class="currency-filter-select">
              <option v-for="c in availableCurrencies" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="info-banner">
            Showing spend for internal Microsoft tenants (customers matching "Options")
          </div>
          <div class="export-buttons">
            <button class="export-btn" :disabled="exporting" @click="exportCsv">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 13h6m-3-3v6m5 5H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
              </svg>
              {{ exporting === 'csv' ? 'Exporting...' : 'Export CSV' }}
            </button>
            <button class="export-btn" :disabled="exporting" @click="exportPdf">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
              {{ exporting === 'pdf' ? 'Generating...' : 'Export PDF' }}
            </button>
          </div>
        </div>

        <!-- ===== KPIs ===== -->
        <div class="kpi-grid">
          <div class="kpi-hero metric-card">
            <div class="metric-label">Total Internal Spend ({{ selectedCurrency }})</div>
            <div class="metric-value" :title="formatCurrency(totalSpend, selectedCurrency)">{{ formatCompactCurrency(totalSpend, selectedCurrency) }}</div>
            <div class="metric-sub">{{ formatCurrency(totalSpend, selectedCurrency) }}</div>
          </div>
          <MetricCard :label="'Internal Tenants (' + selectedCurrency + ')'" :value="customerCount" :state="'loaded'" />
          <MetricCard label="Top Resource Cost" :value="topResourceName" :state="'loaded'" />
          <MetricCard label="Top Tenant" :value="topCustomerName" :state="'loaded'" />
        </div>

        <!-- ===== Monthly Trend + Resource Spend ===== -->
        <div class="dashboard-grid">
          <div class="card chart-card-wide">
            <h3>Monthly Spend Trend (All Internal Tenants, {{ selectedCurrency }})</h3>
            <div v-if="monthlyChartLabels.length > 0" class="chart-wrapper">
              <BillingBarChart
                :labels="monthlyChartLabels"
                :values="monthlyChartValues"
                label="Total Spend"
                :currency="selectedCurrency + ' '"
                :auto-skip="true"
                :max-ticks-limit="24"
              />
            </div>
            <p v-else class="no-data">No monthly data available</p>
          </div>

          <div class="card">
            <h3>Top Azure Resource Spend ({{ selectedCurrency }})</h3>
            <div v-if="filteredResources.length > 0" class="trend-table">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Region</th>
                    <th style="text-align: right;">Spend</th>
                    <th style="text-align: right;">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(product, idx) in filteredResources" :key="idx" :style="{ borderLeftColor: getRegionColor(product.region_code) }">
                    <td>{{ product.product_name || 'Unknown' }}</td>
                    <td><span class="region-badge">{{ product.region_code }}</span></td>
                    <td class="currency-cell">{{ formatCurrency(product.total_spend, product.currency) }}</td>
                    <td style="text-align: right;">{{ formatCount(product.total_quantity) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="no-data">No resource spend data available</p>
          </div>
        </div>

        <!-- ===== Spend by Customer Table ===== -->
        <div class="card">
          <h3>Spend by Internal Tenant</h3>
          <div v-if="dashboard.totalSpendByCustomer && dashboard.totalSpendByCustomer.length > 0" class="trend-table">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Region</th>
                  <th>Currency</th>
                  <th style="text-align: right;">Spend</th>
                  <th style="text-align: center;">Invoices</th>
                  <th style="text-align: center;">Line Items</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(customer, idx) in dashboard.totalSpendByCustomer" :key="idx" :style="{ borderLeftColor: getRegionColor(customer.region_code) }">
                  <td>{{ customer.customer_name || 'Unknown' }}</td>
                  <td><span class="region-badge">{{ customer.region_code }}</span></td>
                  <td>{{ customer.currency || '--' }}</td>
                  <td class="currency-cell">{{ formatCurrency(customer.total_spend, customer.currency) }}</td>
                  <td style="text-align: center;">{{ customer.invoice_count }}</td>
                  <td style="text-align: center;">{{ customer.line_item_count }}</td>
                  <td>
                    <button class="summary-btn" @click="viewCustomerDetail(customer)">View Details</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No customer spend data available</p>
        </div>

        <!-- ===== Product Spend (All Products) ===== -->
        <div class="card">
          <div class="card-header-row">
            <h3>Product Spend (All Internal Tenants)</h3>
            <label class="toggle-label">
              <input type="checkbox" v-model="resourceOnly" @change="loadProductSpend" />
              Resource products only
            </label>
          </div>
          <div v-if="productSpendState === 'loading'" class="loading-state">
            <span>Loading product spend...</span>
          </div>
          <div v-else-if="productSpend && productSpend.length > 0" class="trend-table">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Region</th>
                  <th>Currency</th>
                  <th style="text-align: right;">Spend</th>
                  <th style="text-align: right;">Qty</th>
                  <th style="text-align: center;">Customers</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, idx) in productSpend" :key="idx" :style="{ borderLeftColor: getRegionColor(product.region_code) }">
                  <td>{{ product.product_name || 'Unknown' }}</td>
                  <td><span class="region-badge">{{ product.region_code }}</span></td>
                  <td>{{ product.currency || '--' }}</td>
                  <td class="currency-cell">{{ formatCurrency(product.total_spend, product.currency) }}</td>
                  <td style="text-align: right;">{{ formatCount(product.total_quantity) }}</td>
                  <td style="text-align: center;">{{ product.customer_count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No product spend data available</p>
        </div>
      </template>
    </div>

    <!-- ===== Customer Detail Modal ===== -->
    <div v-if="detailVisible" class="modal-overlay" @click="closeDetail">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header">
          <h2>{{ detailCustomer ? detailCustomer.customer_name : 'Customer Detail' }}</h2>
          <button class="modal-close" @click="closeDetail">&times;</button>
        </div>
        <div v-if="detailState === 'loading'" class="loading-state">
          <span>Loading customer details...</span>
        </div>
        <div v-else-if="detailState === 'error'" class="error-message">
          {{ detailError }}
        </div>
        <div v-else class="modal-body">
          <!-- Customer info -->
          <div class="detail-info-row" v-if="detailCustomer">
            <span class="detail-label">Domain:</span>
            <span>{{ detailCustomer.customer_domain || '--' }}</span>
            <span class="detail-label">Region:</span>
            <span><span class="region-badge">{{ detailCustomer.region_code }}</span></span>
            <span class="detail-label">Country:</span>
            <span>{{ detailCustomer.customer_country || '--' }}</span>
          </div>

          <!-- Summary KPIs -->
          <div class="summary-kpi-grid" v-if="detailSummary">
            <div class="kpi-mini">
              <div class="kpi-mini-label">Total Spend</div>
              <div class="kpi-mini-value">{{ formatCurrency(detailSummary.total_spend) }}</div>
            </div>
            <div class="kpi-mini">
              <div class="kpi-mini-label">Invoices</div>
              <div class="kpi-mini-value">{{ detailSummary.invoice_count }}</div>
            </div>
            <div class="kpi-mini">
              <div class="kpi-mini-label">Products</div>
              <div class="kpi-mini-value">{{ detailSummary.product_count }}</div>
            </div>
          </div>

          <!-- Resource spend -->
          <h4 class="detail-section-title">Azure Resource Spend</h4>
          <div v-if="detailData.resourceSpend && detailData.resourceSpend.length > 0" class="trend-table">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Spend</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, idx) in detailData.resourceSpend" :key="idx">
                  <td>{{ product.product_name || 'Unknown' }}</td>
                  <td class="currency-cell">{{ formatCurrency(product.total_spend, product.currency) }}</td>
                  <td>{{ formatCount(product.total_quantity) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No resource spend for this tenant</p>

          <!-- Monthly trend -->
          <h4 class="detail-section-title">Monthly Spend Trend</h4>
          <div v-if="detailData.monthlySpend && detailData.monthlySpend.length > 0" class="trend-table">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Currency</th>
                  <th>Spend</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(month, idx) in detailData.monthlySpend" :key="idx">
                  <td>{{ formatMonth(month.billing_month) }}</td>
                  <td>{{ month.currency || '--' }}</td>
                  <td class="currency-cell">{{ formatCurrency(month.total_spend, month.currency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No monthly data for this tenant</p>

          <!-- Line items tab -->
          <div class="detail-tabs">
            <button class="detail-tab" :class="{ active: detailTab === 'summary' }" @click="detailTab = 'summary'">Summary</button>
            <button class="detail-tab" :class="{ active: detailTab === 'lineItems' }" @click="switchToLineItems">Line Items</button>
          </div>

          <div v-if="detailTab === 'lineItems'">
            <div v-if="lineItemsState === 'loading'" class="loading-state">
              <span>Loading line items...</span>
            </div>
            <div v-else-if="lineItems && lineItems.length > 0" class="trend-table">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Charge Type</th>
                    <th>Qty</th>
                    <th>Total</th>
                    <th>Charge Start</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in lineItems" :key="idx">
                    <td>{{ item.product_name || '--' }}</td>
                    <td>{{ item.sku_name || '--' }}</td>
                    <td>{{ item.charge_type || '--' }}</td>
                    <td>{{ formatCount(item.quantity) }}</td>
                    <td class="currency-cell">{{ formatCurrency(item.total, item.currency) }}</td>
                    <td>{{ formatDate(item.charge_start_date, 'short') }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="pagination" v-if="lineItemsTotalPages > 1">
                <button class="page-btn" :disabled="lineItemsPage <= 1" @click="loadLineItems(lineItemsPage - 1)">Previous</button>
                <span class="page-info">Page {{ lineItemsPage }} of {{ lineItemsTotalPages }}</span>
                <button class="page-btn" :disabled="lineItemsPage >= lineItemsTotalPages" @click="loadLineItems(lineItemsPage + 1)">Next</button>
              </div>
            </div>
            <p v-else class="no-data">No line items found</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import BillingBarChart from '@/components/billing/BillingBarChart.vue';
import {
  getInternalBillingDashboard,
  getInternalCustomerSpend,
  getInternalCustomerLineItems,
  getInternalProductSpend,
  downloadInternalBillingCsv,
  downloadInternalBillingPdf
} from '@/services/billing-api.js';
import { formatCurrency, formatCount, formatCompactCurrency, getRegionColor } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';

export default {
  name: 'InternalBilling',
  components: { EntraPageHeader, MetricCard, BillingBarChart },
  data: function() {
    return {
      dashboard: {},
      loading: true,
      error: '',
      selectedRange: 6,
      timeRanges: [
        { label: '1 Month', value: 1 },
        { label: '3 Months', value: 3 },
        { label: '6 Months', value: 6 },
        { label: '1 Year', value: 12 },
        { label: '2 Years', value: 24 },
        { label: 'All', value: 60 }
      ],
      resourceOnly: false,
      selectedCurrency: 'GBP',
      productSpend: [],
      productSpendState: 'loaded',
      exporting: false,
      detailVisible: false,
      detailCustomer: null,
      detailData: {},
      detailSummary: null,
      detailState: 'loaded',
      detailError: '',
      detailTab: 'summary',
      lineItems: [],
      lineItemsState: 'loaded',
      lineItemsPage: 1,
      lineItemsPageSize: 50,
      lineItemsTotal: 0
    };
  },
  computed: {
    availableCurrencies: function() {
      var seen = {};
      var add = function(c) { if (c) seen[c] = true; };
      (this.dashboard.totalSpendByCustomer || []).forEach(function(r) { add(r.currency); });
      (this.dashboard.monthlySpend || []).forEach(function(r) { add(r.currency); });
      (this.dashboard.resourceSpend || []).forEach(function(r) { add(r.currency); });
      var list = Object.keys(seen).sort();
      if (list.length === 0) list = [this.selectedCurrency];
      return list;
    },
    filteredCustomers: function() {
      var self = this;
      return (this.dashboard.totalSpendByCustomer || []).filter(function(r) {
        return (r.currency || '') === self.selectedCurrency;
      });
    },
    filteredResources: function() {
      var self = this;
      return (this.dashboard.resourceSpend || []).filter(function(r) {
        return (r.currency || '') === self.selectedCurrency;
      });
    },
    totalSpend: function() {
      var total = 0;
      for (var i = 0; i < this.filteredCustomers.length; i++) {
        total += Number(this.filteredCustomers[i].total_spend || 0);
      }
      return total;
    },
    customerCount: function() {
      var seen = {};
      var count = 0;
      for (var i = 0; i < this.filteredCustomers.length; i++) {
        var id = this.filteredCustomers[i].customer_id;
        if (id && !seen[id]) {
          seen[id] = true;
          count++;
        }
      }
      return count;
    },
    topCustomerName: function() {
      if (this.filteredCustomers.length === 0) return '--';
      return this.filteredCustomers[0].customer_name || '--';
    },
    topResourceName: function() {
      if (this.filteredResources.length === 0) return '--';
      return this.filteredResources[0].product_name || '--';
    },
    monthlyBuckets: function() {
      if (!this.dashboard.monthlySpend) return [];
      var self = this;
      var byMonth = {};
      for (var i = 0; i < this.dashboard.monthlySpend.length; i++) {
        var row = this.dashboard.monthlySpend[i];
        if ((row.currency || '') !== self.selectedCurrency) continue;
        var key = String(row.billing_month).slice(0, 7);
        byMonth[key] = (byMonth[key] || 0) + Number(row.total_spend || 0);
      }
      var keys = Object.keys(byMonth).sort();
      return keys.map(function(k) { return { key: k, value: byMonth[k] }; });
    },
    monthlyChartLabels: function() {
      var self = this;
      return this.monthlyBuckets.map(function(b) { return self.formatMonth(b.key + '-01'); });
    },
    monthlyChartValues: function() {
      return this.monthlyBuckets.map(function(b) { return b.value; });
    },
    lineItemsTotalPages: function() {
      return Math.ceil(this.lineItemsTotal / this.lineItemsPageSize) || 1;
    }
  },
  methods: {
    formatCurrency: function(amount, currency) {
      return formatCurrency(amount, currency);
    },
    formatCompactCurrency: function(amount, currency) {
      return formatCompactCurrency(amount, currency);
    },
    getRegionColor: getRegionColor,
    formatCount: formatCount,
    formatDate: formatDate,
    formatMonth: function(monthStr) {
      if (!monthStr) return '--';
      var d = new Date(monthStr);
      if (isNaN(d.getTime())) return '--';
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[d.getUTCMonth()] + " '" + String(d.getUTCFullYear()).slice(2);
    },
    selectRange: function(range) {
      if (this.loading || this.productSpendState === 'loading') return;
      this.selectedRange = range;
      this.loadAll();
    },
    loadAll: function() {
      this.loadDashboard();
      this.loadProductSpend();
    },
    loadDashboard: function() {
      var self = this;
      this.error = '';
      this.loading = true;
      getInternalBillingDashboard({ months: this.selectedRange }).then(function(data) {
        self.dashboard = data;
        self.loading = false;
      }).catch(function(err) {
        console.error('Failed to load internal billing dashboard:', err);
        self.error = err.message || 'Failed to load billing data.';
        self.loading = false;
      });
    },
    loadProductSpend: function() {
      var self = this;
      this.productSpendState = 'loading';
      getInternalProductSpend({ months: this.selectedRange, resourceOnly: this.resourceOnly }).then(function(data) {
        self.productSpend = data.items || [];
        self.productSpendState = 'loaded';
      }).catch(function(err) {
        console.error('Failed to load product spend:', err);
        self.productSpend = [];
        self.productSpendState = 'loaded';
      });
    },
    viewCustomerDetail: function(customer) {
      var self = this;
      this.detailVisible = true;
      this.detailCustomer = customer;
      this.detailData = {};
      this.detailSummary = null;
      this.detailState = 'loading';
      this.detailError = '';
      this.detailTab = 'summary';
      this.lineItems = [];

      getInternalCustomerSpend(customer.customer_id, { months: this.selectedRange }).then(function(data) {
        self.detailCustomer = data.customer;
        self.detailSummary = data.summary;
        self.detailData = data;
        self.detailState = 'loaded';
      }).catch(function(err) {
        self.detailState = 'error';
        self.detailError = err.message || 'Failed to load customer details.';
      });
    },
    closeDetail: function() {
      this.detailVisible = false;
      this.detailCustomer = null;
      this.detailData = {};
      this.detailSummary = null;
      this.lineItems = [];
    },
    switchToLineItems: function() {
      this.detailTab = 'lineItems';
      if (this.lineItems.length === 0) {
        this.loadLineItems(1);
      }
    },
    loadLineItems: function(page) {
      var self = this;
      if (!this.detailCustomer) return;
      this.lineItemsPage = page;
      this.lineItemsState = 'loading';
      getInternalCustomerLineItems(this.detailCustomer.customer_id, {
        page: page,
        pageSize: this.lineItemsPageSize
      }).then(function(data) {
        self.lineItems = data.items || [];
        self.lineItemsTotal = data.total || 0;
        self.lineItemsState = 'loaded';
      }).catch(function(err) {
        console.error('Failed to load line items:', err);
        self.lineItems = [];
        self.lineItemsState = 'loaded';
        self.error = 'Failed to load line items: ' + (err.message || 'Unknown error');
      });
    },
    exportCsv: function() {
      var self = this;
      this.exporting = 'csv';
      downloadInternalBillingCsv({ months: this.selectedRange }).then(function(response) {
        self.downloadBlob(response, 'csv');
        self.exporting = false;
      }).catch(function(err) {
        console.error('CSV export failed:', err);
        self.exporting = false;
        self.error = 'CSV export failed: ' + (err.message || 'Unknown error');
      });
    },
    exportPdf: function() {
      var self = this;
      this.exporting = 'pdf';
      downloadInternalBillingPdf({ months: this.selectedRange }).then(function(response) {
        self.downloadBlob(response, 'pdf');
        self.exporting = false;
      }).catch(function(err) {
        console.error('PDF export failed:', err);
        self.exporting = false;
        self.error = 'PDF export failed: ' + (err.message || 'Unknown error');
      });
    },
    downloadBlob: function(response, type) {
      var dateStr = new Date().toISOString().split('T')[0];
      var filename = 'internal_billing_' + (type === 'pdf' ? 'report' : 'spend') + '_' + dateStr + '.' + type;
      var disposition = response.headers && response.headers['content-disposition'];
      if (disposition) {
        var match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      var blob = new Blob([response.data], { type: type === 'pdf' ? 'application/pdf' : 'text/csv' });
      var url = window.URL.createObjectURL(blob);
      var link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  },
  mounted: function() {
    this.loadAll();
  }
};
</script>

<style scoped>
.internal-billing {
  min-height: 100%;
}

.page-body {
  padding: 24px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.info-banner {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  background: #f1f5f9;
  padding: 6px 14px;
  border-radius: 8px;
}

.export-buttons {
  display: flex;
  gap: 8px;
}
.export-btn {
  background: #fff;
  color: #334155;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
}
.export-btn:hover:not(:disabled) {
  background: #4f46e5;
  color: #fff;
  border-color: #4f46e5;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
}
.export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.range-tabs {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
}
.range-tab {
  padding: 6px 14px;
  border: none;
  background: transparent;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
}
.range-tab:hover {
  color: #334155;
  background: #e2e8f0;
}
.range-tab.active {
  background: #ffffff;
  color: #4f46e5;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.currency-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}
.currency-filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}
.currency-filter-select {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  font-family: 'Inter', sans-serif;
  color: #334155;
  background: #ffffff;
}

.kpi-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}
.kpi-grid :deep(.metric-card) {
  padding-left: 16px;
}
.kpi-grid :deep(.metric-value) {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.2;
}
.kpi-grid :deep(.metric-label) {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.kpi-hero {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.kpi-hero .metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.kpi-hero .metric-value {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #0f172a;
  line-height: 1.1;
  margin-top: 4px;
}
.kpi-hero .metric-sub {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin-top: 4px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  align-items: start;
}

.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  margin-bottom: 20px;
}
.card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  transform: translateY(-2px);
}
.card h3 {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.card-header-row h3 {
  margin: 0;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
}
.toggle-label input {
  cursor: pointer;
}

.chart-card-wide {
  grid-column: 1 / -1;
}

.chart-wrapper {
  height: 280px;
  position: relative;
  background: #fafafa;
  border-radius: 8px;
  padding: 8px;
}

.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8125rem;
}
.data-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 700;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e2e8f0;
}
.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}
.data-table tbody tr {
  border-left: 3px solid transparent;
  transition: background 0.15s;
}
.data-table tbody tr:nth-child(even) {
  background: #fafafa;
}
.data-table tbody tr:hover {
  background: #f8fafc;
}
.data-table tbody tr:last-child td {
  border-bottom: none;
}

.currency-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #0f172a;
}

.region-badge {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.75rem;
  border: 1px solid #dbeafe;
  display: inline-block;
}

.summary-btn {
  background: #f1f5f9;
  color: #4f46e5;
  border: 1px solid #e2e8f0;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: all 0.15s;
}
.summary-btn:hover {
  background: #4f46e5;
  color: #fff;
  border-color: #4f46e5;
}

.no-data {
  font-size: 0.875rem;
  color: #94a3b8;
  text-align: center;
  padding: 32px 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
  margin-top: 8px;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 16px;
  border-radius: 8px;
  font-size: 0.875rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  color: #64748b;
  font-size: 0.875rem;
}

/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 12px;
  max-width: 800px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}
.modal-large {
  max-width: 1000px;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}
.modal-close:hover {
  color: #0f172a;
}
.modal-body {
  padding: 20px 24px;
}

.detail-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 20px;
  font-size: 0.8125rem;
  align-items: center;
}
.detail-label {
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  font-size: 0.6875rem;
  letter-spacing: 0.04em;
}

.summary-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}
.kpi-mini {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
}
.kpi-mini-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.kpi-mini-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.detail-section-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #0f172a;
  margin: 20px 0 10px 0;
}

.detail-tabs {
  display: flex;
  gap: 4px;
  margin-top: 24px;
  margin-bottom: 12px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
  width: fit-content;
}
.detail-tab {
  padding: 6px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.detail-tab.active {
  background: #fff;
  color: #4f46e5;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}
.page-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-info {
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 500;
}

@media (max-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .kpi-grid {
    grid-template-columns: 1fr 1fr;
  }
  .summary-kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
